import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";

interface ToastProps {
  message: string;
  duration?: number;
  onClose?: () => void;
}

export function Toast({ message, duration = 2000, onClose }: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
      >
        <div
          className="
            inline-flex justify-center items-center gap-[10px] 
            px-[12px] py-[12px] rounded-[8px] max-w-[223px]
            font-['Mulish'] text-[14px] font-normal leading-[120%] text-center
            bg-[#333333] text-white
          "
        >
          {message}
        </div>
      </motion.div>
    </div>
  );
}

// Toast 管理器
export const toast = {
  _container: null as HTMLDivElement | null,

  show(props: ToastProps) {
    if (typeof window === "undefined") return;

    if (!this._container) {
      this._container = document.createElement("div");
      document.body.appendChild(this._container);
    }

    const handleClose = () => {
      if (this._container) {
        const unmount = () => {
          if (this._container) {
            document.body.removeChild(this._container);
            this._container = null;
          }
        };
        props.onClose?.();
        unmount();
      }
    };

    const portal = createPortal(
      <AnimatePresence>
        <Toast {...props} onClose={handleClose} />
      </AnimatePresence>,
      this._container
    );

    const root = document.createElement("div");
    this._container.appendChild(root);
    createRoot(root).render(portal);
  },

  success(message: string, duration?: number) {
    this.show({ message, duration });
  },

  error(message: string, duration?: number) {
    this.show({ message, duration });
  },

  info(message: string, duration?: number) {
    this.show({ message, duration });
  },
};
